/**
 * lib/content-routing — контент как маршруты SPA (один origin, тот же рейл).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: ADR-0020 §«Сшить контент в приложение :5173» —
 * контент-страницы (лендинг/правила/гайд/стратегия/FAQ/about, ru+en) живут
 * ВНУТРИ работающего клиента как часть того же приложения с тем же рейлом, НЕ
 * отдельный сайт. Этот модуль — ЕДИНЫЙ ствол контент-роутинга (модель ствол→ветки):
 *   - читает сгенерированный билдом контент-бандл (тот же источник, что краулер-
 *     статика → контент совпадает);
 *   - резолвит origin-relative путь → страницу (через @lines98/shared content-routes,
 *     общий с SSG → пути совпадают);
 *   - строит nav-модель (разделы текущего языка + lang-switcher);
 *   - `navigateContent()` — переход на контент-страницу + pushState (URL в адресной
 *     строке = тот же путь, что у краулера; контент-навигация НЕ отложена — это
 *     Фаза 0-2, отложена только Фаза 3 — per-route URL ИГРОВЫХ экранов).
 *
 * SCOPE: чтение бандла, резолв пути↔страница, nav-модель, History pushState для
 *   контент-переходов, разбор boot-пути. Vanilla TS, без фреймворка-роутера.
 * NOT IN SCOPE: рендер DOM страницы (screens/content-screen), монтаж рейла (app.ts),
 *   игровой роутинг (Фаза 3 DEFERRED), генерация бандла (scripts/build-content).
 *
 * Cross-references: ADR-0020 §«Сшить контент» + §«Ссылки origin-relative»;
 *   @lines98/shared/config/content-routes (URL-каркас); generated/content-bundle.
 */

import {
  CONTENT_LANGS,
  DEFAULT_CONTENT_LANG,
  LANG_LABELS,
  UI_LANGS,
  type ContentLang,
  type UiLang,
} from '@lines98/shared/config/seo';
import {
  contentPath,
  defaultLandingPath,
  normalizeSlug,
  parseContentPath,
} from '@lines98/shared/config/content-routes';

import {
  CONTENT_BUNDLE,
  type ContentBundleLang,
  type ContentBundlePage,
} from '../generated/content-bundle.js';
import { useGameStore } from '../store/game-store.js';

export type { ContentBundlePage };

/** Тип-гард: строка — один из контент-языков. */
export function isContentLang(s: string): s is ContentLang {
  return (CONTENT_LANGS as readonly string[]).includes(s);
}

/** Данные одного языка из бандла (ui + страницы). null если язык отсутствует. */
function langBundle(lang: string): ContentBundleLang | null {
  return CONTENT_BUNDLE[lang] ?? null;
}

/**
 * Резолв страницы по (lang, slug). Терпим: неизвестный язык → дефолт-язык;
 * неизвестный slug → лендинг этого языка (slug ''). Никогда не кидает — контент-
 * фаза всегда что-то рендерит (нет «битых» внутренних переходов).
 */
export function resolveContentPage(
  lang: string,
  slug: string,
): { lang: ContentLang; page: ContentBundlePage; ui: ContentBundleLang['ui'] } {
  const safeLang: ContentLang = isContentLang(lang) ? lang : DEFAULT_CONTENT_LANG;
  const bundle = langBundle(safeLang) ?? langBundle(DEFAULT_CONTENT_LANG)!;
  const wantSlug = normalizeSlug(slug);
  const page =
    bundle.pages.find((p) => p.slug === wantSlug) ??
    bundle.pages.find((p) => p.slug === '') ??
    bundle.pages[0]!;
  return { lang: safeLang, page, ui: bundle.ui };
}

/** Один пункт контент-навигации (origin-relative href + флаг active). */
export interface ContentNavLink {
  readonly slug: string;
  readonly label: string;
  readonly href: string;
  readonly active: boolean;
}

/**
 * Разделы текущего языка (по navOrder) с active-флагом для текущего slug. Скрытые
 * из навигации страницы (`navHidden` — юр-страницы вроде Privacy Policy: они в
 * футере, не в оглавлении «Помощи») ИСКЛЮЧАЮТСЯ; на маршрутизацию/рендер самой
 * страницы это не влияет (resolveContentPage по-прежнему её отдаёт по прямому пути).
 */
export function contentNavLinks(lang: ContentLang, activeSlug: string): ContentNavLink[] {
  const bundle = langBundle(lang) ?? langBundle(DEFAULT_CONTENT_LANG)!;
  const want = normalizeSlug(activeSlug);
  return bundle.pages
    .filter((p) => !p.navHidden)
    .map((p) => ({
      slug: p.slug,
      label: p.navLabel,
      href: contentPath(lang, p.slug),
      active: p.slug === want,
    }));
}

/** Языковая ссылка для switcher'а (тот же slug на другом языке, если он есть). */
export interface ContentLangChoice {
  readonly lang: ContentLang;
  readonly label: string;
  readonly href: string;
  readonly active: boolean;
}

/**
 * Языки, у которых ЕСТЬ страница с данным slug (симметрия, как в SSG). Каждый —
 * origin-relative href на ту же страницу другого языка + native-эндоним.
 */
export function contentLangChoices(slug: string, activeLang: ContentLang): ContentLangChoice[] {
  const want = normalizeSlug(slug);
  const out: ContentLangChoice[] = [];
  for (const lang of CONTENT_LANGS) {
    if (langBundle(lang)?.pages.some((p) => p.slug === want)) {
      out.push({
        lang,
        label: LANG_LABELS[lang] ?? lang,
        href: contentPath(lang, want),
        active: lang === activeLang,
      });
    }
  }
  return out;
}

/**
 * Перейти на контент-страницу ВНУТРИ приложения + обновить адресную строку
 * (pushState) на origin-relative путь по URL-МОДЕЛИ B (ADR-0021 addendum-001 §A/§D).
 * Контент-навигация НЕ отложена. `replace=true` — заменить запись (boot / смена языка
 * рейлом), без новой записи в истории.
 *
 * МОДЕЛЬ B: путь строится `contentPath` (единый источник) — английский БЕЗ префикса
 * (`/`, `/<slug>/`), остальные с префиксом (`/ru/`, `/de/<slug>/`). URL несёт
 * ЗАПРОШЕННЫЙ язык, если он входит в `UI_LANGS` (12) — ВКЛЮЧАЯ языки-заглушки
 * (`/de/<slug>/`), у которых нет индексируемого контента; контент рендерится на
 * en-фолбэке (`resolveContentPage`), но видимый URL остаётся `/de/<slug>/`
 * (canonical→корень-en + noindex — SSG/прод-слой T-I18N-9, не SPA). Slug сохраняется
 * КАК ЗАПРОШЕН (тот же slug при смене языка). Неизвестный (не из UI_LANGS) язык → en
 * (корень). Пустой `lang` (`''`) трактуется как дефолт-en (вызов из рейла «Помощь»).
 *
 * ЗАЩИТНЫЙ ПОЯС НА ФАНТОМ-ЛЕНДИНГ (fix/seo-hardening, owner-bug 2026-06-19): slug `''`
 * = `landing.md` = SSG-зеркало ГЛАВНОЙ, а НЕ навигируемая контент-СТАТЬЯ. Контент-фаза
 * НИКОГДА не должна рендерить slug `''` (иначе «Помощь»/устаревшая крошка на `''`
 * показала бы фантом-лендинг внутри content-screen вместо настоящей скролл-главной).
 * Поэтому при ПУСТОМ slug navigateContent УВОДИТ на скролл-лендинг (start-фаза + URL
 * модели B), а не на showContent(''). Belt-and-suspenders: даже устаревшая ссылка на
 * `''` не покажет фантом. Делегируем в `navigateLandingSpa` (единый ствол главной).
 */
export function navigateContent(lang: string, slug: string, replace = false): void {
  // Видимый язык URL = запрошенный, если он валидный UI-язык (вкл. заглушки); иначе en.
  const urlLang = (UI_LANGS as readonly string[]).includes(lang)
    ? (lang as (typeof UI_LANGS)[number])
    : DEFAULT_CONTENT_LANG;
  const urlSlug = normalizeSlug(slug);
  // ЗАЩИТНЫЙ ПОЯС: пустой slug = главная, НЕ контент-статья → скролл-лендинг (start).
  if (urlSlug === '') {
    navigateLandingSpa(urlLang, replace);
    return;
  }
  // Путь — через ЕДИНЫЙ источник contentPath (модель B: en в корне без префикса).
  const path = contentPath(urlLang, urlSlug);
  if (typeof window !== 'undefined' && window.history) {
    const fn = replace ? 'replaceState' : 'pushState';
    // Сохраняем маркер контент-маршрута в state — popstate отличит контент от игры.
    window.history[fn]({ content: { lang: urlLang, slug: urlSlug } }, '', path);
  }
  // Store/рендер хранят запрошенный язык+slug; content-screen рендерит через
  // resolveContentPage (тот сам фолбэчит на en для заглушек/неизвестных slug).
  useGameStore.getState().showContent(urlLang, urlSlug);
}

/**
 * Перейти на СКРОЛЛ-ЛЕНДИНГ (единая индексируемая ГЛАВНАЯ, start-фаза) на языке `lang`
 * + записать адрес по URL-модели B (en→`/`, ru→`/ru/`) через ЕДИНЫЙ источник
 * `contentPath`. Используется защитным поясом navigateContent (пустой slug) и
 * content-screen'ом (клик по ссылке-лендингу). Маркер `landing` в history-state —
 * popstate (app.ts) распознаёт главную. Глобальный `locale` = язык ссылки (URL
 * авторитетен) → стартовая + рейл следуют ему. НЕ рендерит slug '' в content-фазе.
 */
export function navigateLandingSpa(lang: UiLang, replace = false): void {
  const path = contentPath(lang, ''); // модель B: en→`/`, ru→`/ru/`
  if (typeof window !== 'undefined' && window.history) {
    const fn = replace ? 'replaceState' : 'pushState';
    window.history[fn]({ landing: { lang } }, '', path);
  }
  const store = useGameStore.getState();
  if (store.locale !== lang) store.setLocale(lang); // URL-язык → глобальный источник
  store.showStart(); // скролл-лендинг (start-фаза), НЕ content-screen
}

/**
 * Разобрать boot-pathname в контентный маршрут под URL-МОДЕЛЬ B (ADR-0021
 * addendum-001 §A). Возвращает:
 *   - `{ kind: 'root' }` — голый корень `/` (особый boot-кейс: редирект по языку
 *     браузера на не-en → `/<lang>/`, иначе en-лендинг остаётся на `/`, T-I18N-8);
 *   - `{ kind: 'content', lang, slug }` — любой иной путь (под моделью B en = корень,
 *     поэтому ВСЁ контентно: `/rules` → en/rules, `/ru/` → ru-лендинг, `/de/x` → de-stub).
 *
 * ⚠ Под моделью B этот парсер НЕ отличает контент от ИГРОВЫХ путей (`/play`, `/daily`)
 * и реплея (`/r/<id>`) — все они распарсились бы как en-slug. Поэтому caller (app.ts
 * boot/popstate) ОБЯЗАН классифицировать реплей и игровые пути ДО вызова этого
 * парсера (порядок: replay → game → root → content). Это явный контракт модели B
 * (en-в-корне): «контентный fallback — последний».
 */
export function classifyBootPath(
  pathname: string,
): { kind: 'content'; lang: string; slug: string } | { kind: 'root' } {
  if (pathname === '' || pathname === '/') return { kind: 'root' };
  const parsed = parseContentPath(pathname);
  return { kind: 'content', lang: parsed.lang, slug: parsed.slug };
}

/** Путь дефолт-лендинга (модель B: корень `/`). */
export { defaultLandingPath };
